/*    
<summary>
  This functional component "AddWorkingDays" allows store to change/delete/add working days which are visible to customer.
</summary>
<returns>Returns JSX</returns>
*/
import dayjs from "dayjs";
import { ErrorMessage, Field, FieldArray, Form, Formik } from "formik";
import { observer } from "mobx-react";
import { Button, Modal } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import weekdayEnum from "../../../../constants/enums/weekday-enum";
import { WorkingHourVM } from "../../../../models/response/IShiftManagementResponse";
import LoaderButton from "../../../../shared-components/button/loader-button";
import FormikFormCheckBox from "../../../../shared-components/FormikFormHandlers/FormikFormCheckBox";
import FormikFormTextArea from "../../../../shared-components/FormikFormHandlers/FormikFormTextArea";
import FormikFormTimePicker from "../../../../shared-components/FormikFormHandlers/FormikFormTimePicker";
import Label from "../../../../shared-components/label/label";
import { formatMessage } from "../../../../translations/format-message";
import FormLoader from "../../../../shared-components/FormLoader/FormLoader";
import { addCmsWorkingDaysValidationSchema } from "../../../../shared-components/FormikFormHandlers/FormikValidationSchema";
interface IProps {
  id: number;
  storeId: number;
  initialValues: any;
  isLoading: boolean;
  submitHandler: (data: any) => void;
  modalClosed: () => void;
}

const AddWorkingDaysForm = (props: IProps) => {
  return (
    <>
      <Modal show={true} size="sm" centered>
        <Modal.Header>
          <Modal.Title>
            <FormattedMessage id="work_hours" />
            <button onClick={props.modalClosed} className="Crossicons">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="23"
                height="23"
                fill="currentColor"
                className="bi bi-x"
                viewBox="0 0 16 16"
              >
                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
              </svg>
            </button>
          </Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={props.initialValues}
          validationSchema={addCmsWorkingDaysValidationSchema}
          validateOnBlur={true}
          validateOnChange={true}
          enableReinitialize
          onSubmit={async (values, { setSubmitting }) => {
            let dataArr: WorkingHourVM[] = [];
            const formattedBusinessSettings = values.BusinessSettings.map(
              (setting: WorkingHourVM, index: number) =>
                dataArr.push({
                  StoreId: props.storeId,
                  WeekDays: setting.WeekDays,
                  IsClosed: setting.IsClosed,
                  IsVisible: setting.IsVisible,
                  OpenTime:
                    setting.IsClosed == true
                      ? "00:00:00"
                      : setting.OpenTime
                      ? dayjs(setting.OpenTime, "HH:mm").format("HH:mm:ss")
                      : "00:00:00",
                  ClosedTime:
                    setting.IsClosed == true
                      ? "00:00:00"
                      : setting.ClosedTime
                      ? dayjs(setting.ClosedTime, "HH:mm").format("HH:mm:ss")
                      : "00:00:00",
                  HolidayMessage: "",
                })
            );

            dataArr.push({
              StoreId: props.storeId,
              WeekDays: 8,
              IsClosed: false,
              IsVisible: values.IsHolidayVisible,
              OpenTime: "00:00:00",
              ClosedTime: "00:00:00",
              HolidayMessage: values.HolidaysMessage,
            });

            dataArr.push({
              StoreId: props.storeId,
              WeekDays: 9,
              IsClosed: false,
              IsVisible: values.IsVisible == "0" ? false : true,
              OpenTime: "00:00:00",
              ClosedTime: "00:00:00",
              HolidayMessage: "",
            });

            let finalObj = {
              CmsWorkingHoursDetails: dataArr,
            };

            let updateObj = {
              StoreId: props.storeId,
              CmsWorkingHoursDetails: dataArr,
            };

            const isUpdateMode = props.id !== undefined && props.id > 0;

            props.submitHandler(isUpdateMode ? updateObj : finalObj);
          }}
        >
          {({ setFieldValue, values, errors, touched }) => (
            <Form className="user-form">
              <Modal.Body>
                {props.isLoading ? (
                  <FormLoader loading={props.isLoading} />
                ) : null}
                {/* Row 1: Shift Name and Shift Short Name */}
                <div className="row g-0">
                  <div className="col-12 form-group">
                    <div className="row g-0">
                      <div className="col-sm-6 col-12">
                        <Label
                          className="label-style"
                          required={true}
                          label="work_hour_visibility"
                        />
                      </div>
                      <div className="col-sm-3 col-6">
                        <Field
                          className="form-check-input"
                          name="IsVisible"
                          type="radio"
                          value="0"
                          id="visible"
                        />
                        <label className="form-check-label" htmlFor="visible">
                          <FormattedMessage id="visible" />
                        </label>
                      </div>
                      <div className="col-sm-3 col-6">
                        <Field
                          className="form-check-input"
                          name="IsVisible"
                          type="radio"
                          value="1"
                          id="hidden"
                        />
                        <label className="form-check-label" htmlFor="hidden">
                          <FormattedMessage id="hidden" />
                        </label>
                      </div>
                    </div>
                  </div>

                  <FieldArray name="BusinessSettings">
                    {({ insert, remove, push }) => (
                      <>
                        {values.BusinessSettings?.map(
                          (obj: any, index: number) => (
                            <div className="col-12 form-group">
                              <div className="d-flex justify-content-between align-items-center">
                                {/* <Label className="label-style" label={JSON.stringify(weekdayEnum[index])} /> */}
                                {index == 7
                                  ? formatMessage("public_holiday")
                                  : formatMessage(weekdayEnum[index])}
                                <div className="form-check">
                                  <label className="form-check-label" >
                                    <Field
                                      data-testid="defaultSettings"
                                      className="form-check-input mt-0"
                                      name={`BusinessSettings.${index}.IsClosed`}
                                      type="checkbox"
                                      id="isClosed"
                                      as={FormikFormCheckBox}
                                    />
                                    <FormattedMessage id="closed" />
                                  </label>
                                </div>
                                {index == 7 && (
                                  <div className="form-check">
                                    <label >
                                      <Field
                                        className="form-check-input mt-0"
                                        name={`BusinessSettings.${index}.IsVisible`}
                                        id="isVisible"
                                        type="checkbox"
                                        as={FormikFormCheckBox}
                                      />
                                      <FormattedMessage id="isVisible" />
                                    </label>
                                  </div>
                                )}
                              </div>
                              <div className="row g-0">
                                <div className="col-5">
                                  <Field
                                    className="form-control"
                                    name={`BusinessSettings.${index}.OpenTime`}
                                    disabled={
                                      values.BusinessSettings[index].IsClosed
                                    }
                                    placeholder={formatMessage("start_time")}
                                    as={FormikFormTimePicker}
                                  />
                                </div>
                                <div className="col-2 text-center">
                                  <h2 className="m-0 fw-bold">~</h2>
                                </div>
                                <div className="col-5">
                                  <Field
                                    className="form-control"
                                    name={`BusinessSettings.${index}.ClosedTime`}
                                    disabled={
                                      values.BusinessSettings[index].IsClosed
                                    }
                                    placeholder={formatMessage("end_time")}
                                    as={FormikFormTimePicker}
                                  />
                                </div>
                              </div>
                            </div>
                          )
                        )}
                      </>
                    )}
                  </FieldArray>

                  <div className="col-12 form-group">
                    <div className="d-flex justify-content-between align-items-center">
                      <Label
                        className="label-style"
                        required={false}
                        label="holidays_cms"
                      />
                      <div className="form-check">
                        <label>
                          <Field
                            className="form-check-input mt-0"
                            name="IsHolidayVisible"
                            id="isVisible"
                            type="checkbox"
                            as={FormikFormCheckBox}
                          />
                          <FormattedMessage id="isVisible" />
                        </label>
                      </div>
                    </div>
                    <Field
                      className="form-control"
                      name="HolidaysMessage"
                      type="text"
                      placeholder="20_characters_max"
                      as={FormikFormTextArea}
                      rows={3}
                    />
                  </div>
                </div>
              </Modal.Body>

              {/* Footer Buttons */}
              <div className="modal-footer center-content">
                <Button
                  variant="outline-danger"
                  type="button"
                  onClick={props.modalClosed}
                >
                  <i className="bi bi-x-lg me-1"></i>
                  <FormattedMessage id="close" />
                </Button>
                <LoaderButton
                  type="submit"
                  iconName="bi-check-lg"
                  text={props.id > -1 ? "button_update" : "button_save"}
                />
              </div>
            </Form>
          )}
        </Formik>
      </Modal>
    </>
  );
};

export default observer(AddWorkingDaysForm);
